import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Account / profile page — let the logged-in user edit their own name,
// email, phone, and preferred language. Email changes go through with no
// re-verification (see CLAUDE.md decision log) — a warning is surfaced
// when the email field is touched so the user knows what they're doing.

export function AccountProfilePage() {
  const { t } = useTranslation()
  const { user, setUser, loading: authLoading } = useAuth()

  const [name, setName]           = useState('')
  const [email, setEmail]         = useState('')
  const [phone, setPhone]         = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [language, setLanguage]   = useState('ro')
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState(null)
  const [savedAt, setSavedAt]   = useState(null)

  // Hydrate from the AuthContext user once it's known.
  useEffect(() => {
    if (!user) return
    setName(user.name || '')
    setEmail(user.email || '')
    setPhone(user.phone || '')
    setBirthdate(user.birthdate || '')
    setLanguage(user.language || 'ro')
  }, [user])

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user) {
    return <main className="container"><p className="error">{t('auth.errors.not_authenticated')}</p></main>
  }

  const emailDirty = email.trim().toLowerCase() !== (user.email || '').toLowerCase()

  async function save(e) {
    e.preventDefault()
    setError(null); setSavedAt(null); setSaving(true)
    try {
      // Only PATCH fields that actually changed — keeps the audit trail
      // and avoids hitting the UNIQUE constraint with the user's own email.
      const patch = {}
      if (name.trim()                                    !== (user.name     || '').trim())            patch.name     = name.trim()
      if (email.trim().toLowerCase()                     !== (user.email    || '').toLowerCase())     patch.email    = email.trim().toLowerCase()
      if ((phone.trim() || null)                         !== (user.phone    || null))                 patch.phone    = phone.trim()
      if ((birthdate || null)                            !== (user.birthdate || null))                patch.birthdate = birthdate || null
      if (language                                       !== (user.language || 'ro'))                  patch.language = language

      if (Object.keys(patch).length === 0) {
        setError('no_changes')
        return
      }
      const r = await api.me.update(patch)
      setUser(r.user)
      setSavedAt(new Date())
    } catch (err) {
      setError(err.code || 'server')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="container">
      <h1>{t('account.title')}</h1>
      <p className="muted">{t('account.intro')}</p>

      <section className="card form-card">
        <form onSubmit={save} noValidate>
          <label>
            <span>{t('account.name')}</span>
            <input type="text" value={name} required minLength={2}
                   onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <span>{t('account.email')}</span>
            <input type="email" value={email} required
                   onChange={(e) => setEmail(e.target.value)} />
          </label>
          {emailDirty && (
            <p className="muted small" style={{ color: 'var(--warn)' }}>
              {t('account.email_change_warning')}
            </p>
          )}
          <label>
            <span>{t('account.phone')}</span>
            <input type="tel" value={phone}
                   onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label>
            <span>{t('account.birthdate')}</span>
            <input type="date" value={birthdate}
                   onChange={(e) => setBirthdate(e.target.value)} />
          </label>
          <label>
            <span>{t('account.language')}</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="ro">{t('common.language_ro')}</option>
              <option value="en">{t('common.language_en')}</option>
            </select>
          </label>

          {error && (
            <p className="error">
              {t(`account.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
            </p>
          )}
          {savedAt && (
            <p className="flash-ok">
              {t('account.saved_at', { time: savedAt.toLocaleTimeString() })}
            </p>
          )}

          <div className="form-actions">
            <button type="submit" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </section>
    </main>
  )
}
